import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Badge,
  IconButton,
  Drawer,
  Alert,
  Tooltip,
  Chip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  CircularProgress,
  Pagination,
  Stack,
  Divider,
  Card,
  CardContent,
  useTheme,
  alpha
} from '@mui/material';
import {
  BlockRounded as BlockIcon,
  CloseRounded as CloseIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Inventory2Outlined as InventoryIcon,
  RemoveShoppingCartOutlined as OutOfStockIcon,
  WarningAmberRounded as WarningIcon,
  DoNotDisturbOnOutlined as BanIcon,
  CheckCircleOutlineRounded as SuccessIcon,
  LocalShippingOutlined as MovementIcon,
  AssignmentReturnOutlined as ReturnIcon
} from '@mui/icons-material';

const API_BASE = 'https://tbl.zerdegroup.kz/api/v1';

export interface BlockedItem {
  ProductZid: string;
  ProductSku: string;
  ProductName: string;
  Barcode: string;
  PartExists?: boolean;
  PartCoeff?: number;
  StockCount: number;
  StockPrice: number;
  Category?: string;
  Producer?: string;
  Manager?: string;
  NotForMovements: boolean;
  NotForReturns: boolean;
  ProductFolder?: string;
  PriceGroup?: string;
  isOutOfStock?: boolean;
}

interface BlockedStats {
  inStockBlockedCount: number;
  allBlockedCount: number;
}

interface BlockedNomenclatureDrawerProps {
  pharmacyGuid?: string;
}

type TabType = 'in_stock_blocked' | 'out_of_stock' | 'all_blocked';
type BlockFilterType = 'all' | 'movements' | 'returns' | 'both';

const BlockedNomenclatureDrawer: React.FC<BlockedNomenclatureDrawerProps> = ({ pharmacyGuid }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<TabType>('in_stock_blocked');
  const [blockFilter, setBlockFilter] = useState<BlockFilterType>('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(50);

  const [items, setItems] = useState<BlockedItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState<BlockedStats>({ inStockBlockedCount: 0, allBlockedCount: 0 });
  const [loading, setLoading] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);

  // Debounce поиска
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const currentGuid = useMemo(() => {
    return pharmacyGuid || localStorage.getItem('guid') || '';
  }, [pharmacyGuid]);

  const fetchBlockedData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/materials/blocked`, {
        params: {
          pharmacyGuid: currentGuid,
          tab,
          search,
          blockType: blockFilter,
          page,
          limit
        }
      });

      if (response.data.success) {
        setItems(response.data.items || []);
        setTotal(response.data.total || 0);
        setTotalPages(response.data.totalPages || 1);
        if (response.data.stats) {
          setStats(response.data.stats);
        }
      }
    } catch (error) {
      console.error('Ошибка при загрузке заблокированной номенклатуры:', error);
    } finally {
      setLoading(false);
      setInitialLoaded(true);
    }
  }, [currentGuid, tab, search, blockFilter, page, limit]);

  // Загружаем статистику для бейджа при монтировании
  useEffect(() => {
    const fetchBadgeStats = async () => {
      try {
        const response = await axios.get(`${API_BASE}/materials/blocked`, {
          params: {
            pharmacyGuid: currentGuid,
            tab: 'in_stock_blocked',
            limit: 1
          }
        });
        if (response.data.success && response.data.stats) {
          setStats(response.data.stats);
        }
      } catch (e) {
        // silent
      }
    };
    if (currentGuid) {
      fetchBadgeStats();
    }
  }, [currentGuid]);

  useEffect(() => {
    if (open) {
      fetchBlockedData();
    }
  }, [open, fetchBlockedData]);

  const handleTabChange = (_: React.SyntheticEvent, newTab: TabType) => {
    setTab(newTab);
    setPage(1);
  };

  const handleBlockFilterChange = (filter: BlockFilterType) => {
    setBlockFilter(filter);
    setPage(1);
  };

  const getBorderColor = (item: BlockedItem) => {
    if (item.NotForMovements && item.NotForReturns) return theme.palette.error.main;
    if (item.NotForMovements) return '#ed6c02'; // warning
    if (item.NotForReturns) return '#0288d1';   // info
    return theme.palette.divider;
  };

  return (
    <>
      {/* Кнопка в тулбаре */}
      <Tooltip title="Заблокированная номенклатура и остатки">
        <span>
          <IconButton
            onClick={() => setOpen(true)}
            size="small"
            color="default"
            sx={{
              border: '1px solid',
              borderColor: stats.inStockBlockedCount > 0 ? alpha(theme.palette.warning.main, 0.6) : 'divider',
              borderRadius: 1,
              bgcolor: stats.inStockBlockedCount > 0 ? alpha(theme.palette.warning.main, 0.08) : 'transparent',
              color: stats.inStockBlockedCount > 0 ? theme.palette.warning.dark : 'text.secondary',
              '&:hover': {
                bgcolor: stats.inStockBlockedCount > 0 ? alpha(theme.palette.warning.main, 0.15) : 'action.hover'
              }
            }}
          >
            <Badge badgeContent={stats.inStockBlockedCount} color="error" max={999}>
              <BlockIcon fontSize="small" />
            </Badge>
          </IconButton>
        </span>
      </Tooltip>

      {/* Выдвижная боковая панель (адаптивная в процентах) */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '85%', md: '65%', lg: '50%', xl: '42%' },
            minWidth: { sm: '480px' },
            maxWidth: '900px',
            bgcolor: '#fafafa',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        {/* 1. Заголовок */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            bgcolor: '#ffffff',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 1.5,
                bgcolor: 'error.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'error.main'
              }}
            >
              <BlockIcon />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="800" color="text.primary" sx={{ lineHeight: 1.2 }}>
                Заблокированная номенклатура
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Товары с ограничениями в 1С и позиции без остатка
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setOpen(false)} size="small" edge="end">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* 2. Информационный баннер */}
        <Alert severity="info" sx={{ borderRadius: 0, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 500, lineHeight: 1.35 }}>
            Товары с установленными запретами не могут быть добавлены в документы перемещения или возврата.
          </Typography>
        </Alert>

        {/* 3. Вкладки (Tabs) */}
        <Box sx={{ bgcolor: '#ffffff', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              '& .MuiTabs-indicator': {
                height: 3
              }
            }}
          >
            <Tab
              value="in_stock_blocked"
              sx={{ textTransform: 'none', py: 1.5 }}
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <InventoryIcon fontSize="small" />
                  <Typography variant="body2" fontWeight={700}>
                    В наличии в аптеке
                  </Typography>
                  <Chip
                    label={stats.inStockBlockedCount}
                    size="small"
                    color={stats.inStockBlockedCount > 0 ? 'error' : 'default'}
                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                  />
                </Stack>
              }
            />
            <Tab
              value="out_of_stock"
              sx={{ textTransform: 'none', py: 1.5 }}
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <OutOfStockIcon fontSize="small" />
                  <Typography variant="body2" fontWeight={700}>
                    Нет в наличии
                  </Typography>
                </Stack>
              }
            />
            <Tab
              value="all_blocked"
              sx={{ textTransform: 'none', py: 1.5 }}
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <BanIcon fontSize="small" />
                  <Typography variant="body2" fontWeight={700}>
                    Все в сети
                  </Typography>
                  <Chip
                    label={stats.allBlockedCount}
                    size="small"
                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                  />
                </Stack>
              }
            />
          </Tabs>
        </Box>

        {/* 4. Поиск и фильтры по типам блокировок */}
        <Box sx={{ p: 2, bgcolor: '#ffffff', borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Поиск по названию, артикулу, штрихкоду, производителю..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchInput('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
            sx={{ mb: 1.5 }}
          />

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label="Все виды"
              size="small"
              clickable
              color={blockFilter === 'all' ? 'primary' : 'default'}
              variant={blockFilter === 'all' ? 'filled' : 'outlined'}
              onClick={() => handleBlockFilterChange('all')}
              sx={{ fontWeight: 600 }}
            />
            <Chip
              label="Запрет перемещений"
              icon={<MovementIcon fontSize="small" />}
              size="small"
              clickable
              color={blockFilter === 'movements' ? 'warning' : 'default'}
              variant={blockFilter === 'movements' ? 'filled' : 'outlined'}
              onClick={() => handleBlockFilterChange('movements')}
              sx={{ fontWeight: 600 }}
            />
            <Chip
              label="Запрет возвратов"
              icon={<ReturnIcon fontSize="small" />}
              size="small"
              clickable
              color={blockFilter === 'returns' ? 'info' : 'default'}
              variant={blockFilter === 'returns' ? 'filled' : 'outlined'}
              onClick={() => handleBlockFilterChange('returns')}
              sx={{ fontWeight: 600 }}
            />
            <Chip
              label="Полный запрет"
              icon={<BanIcon fontSize="small" />}
              size="small"
              clickable
              color={blockFilter === 'both' ? 'error' : 'default'}
              variant={blockFilter === 'both' ? 'filled' : 'outlined'}
              onClick={() => handleBlockFilterChange('both')}
              sx={{ fontWeight: 600 }}
            />
          </Stack>
        </Box>

        {/* 5. Список карточек позиций */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
          {loading && !initialLoaded ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: 'success.50',
                  color: 'success.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2
                }}
              >
                <SuccessIcon sx={{ fontSize: 32 }} />
              </Box>
              <Typography variant="subtitle1" fontWeight="700" color="text.primary">
                Позиций не найдено
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, mt: 0.5 }}>
                {search
                  ? `По запросу «${search}» ничего не найдено.`
                  : tab === 'in_stock_blocked'
                    ? 'В текущей аптеке нет заблокированных товаров на остатке.'
                    : 'Список пуст.'}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {items.map((item) => {
                const borderLeftColor = getBorderColor(item);
                return (
                  <Card
                    key={`${item.ProductZid}-${item.Barcode}`}
                    elevation={0}
                    sx={{
                      bgcolor: '#ffffff',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderLeft: `5px solid ${borderLeftColor}`,
                      borderRadius: 2,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      {/* Верхняя строка: Название и статус наличия/остатка */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight="700" color="text.primary" sx={{ fontSize: '0.95rem' }}>
                          {item.ProductName}
                        </Typography>

                        {item.StockCount > 0 ? (
                          <Chip
                            label={`Остаток: ${item.StockCount} уп.`}
                            size="small"
                            color="primary"
                            variant="filled"
                            sx={{ fontWeight: 700, fontSize: '0.75rem', height: 24 }}
                          />
                        ) : (
                          <Chip
                            label="Нет в наличии (< 1 уп)"
                            size="small"
                            sx={{
                              bgcolor: 'grey.100',
                              color: 'text.secondary',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 24
                            }}
                          />
                        )}
                      </Box>

                      {/* Чипы блокировок */}
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                        {item.NotForMovements && (
                          <Chip
                            label="Запрет перемещения"
                            size="small"
                            sx={{
                              bgcolor: alpha('#ed6c02', 0.1),
                              color: '#d95300',
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              height: 22,
                              border: '1px solid',
                              borderColor: alpha('#ed6c02', 0.3)
                            }}
                          />
                        )}
                        {item.NotForReturns && (
                          <Chip
                            label="Запрет возврата"
                            size="small"
                            sx={{
                              bgcolor: alpha('#0288d1', 0.1),
                              color: '#0277bd',
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              height: 22,
                              border: '1px solid',
                              borderColor: alpha('#0288d1', 0.3)
                            }}
                          />
                        )}
                        {item.Category && (
                          <Chip
                            label={item.Category}
                            size="small"
                            sx={{
                              bgcolor: 'action.hover',
                              color: 'text.secondary',
                              fontSize: '0.7rem',
                              height: 22
                            }}
                          />
                        )}
                      </Stack>

                      {/* Инфо: Код, Штрихкод, Производитель, Менеджер */}
                      <Box
                        sx={{
                          bgcolor: 'grey.50',
                          p: 1.25,
                          borderRadius: 1.5,
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                          gap: 1,
                          fontSize: '0.75rem',
                          color: 'text.secondary'
                        }}
                      >
                        <Box>
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontWeight: 600 }}>
                            КОД:
                          </Typography>
                          <Typography variant="body2" fontWeight="600" color="text.primary" sx={{ fontSize: '0.8rem' }}>
                            {item.ProductSku || '-'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontWeight: 600 }}>
                            ШТРИХКОД:
                          </Typography>
                          <Typography variant="body2" fontWeight="600" color="text.primary" sx={{ fontSize: '0.8rem' }}>
                            {item.Barcode || '-'}
                          </Typography>
                        </Box>
                        {item.Producer && (
                          <Box>
                            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontWeight: 600 }}>
                              ПРОИЗВОДИТЕЛЬ:
                            </Typography>
                            <Typography variant="body2" color="text.primary" sx={{ fontSize: '0.8rem' }}>
                              {item.Producer}
                            </Typography>
                          </Box>
                        )}
                        {item.Manager && (
                          <Box>
                            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontWeight: 600 }}>
                              МЕНЕДЖЕР:
                            </Typography>
                            <Typography variant="body2" fontWeight="700" color="primary.main" sx={{ fontSize: '0.8rem' }}>
                              {item.Manager}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}
        </Box>

        {/* 6. Пагинация в подвале */}
        {totalPages > 1 && (
          <Box
            sx={{
              p: 2,
              bgcolor: '#ffffff',
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Всего: <b>{total}</b> позиций
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, val) => setPage(val)}
              size="small"
              color="primary"
            />
          </Box>
        )}
      </Drawer>
    </>
  );
};

export default BlockedNomenclatureDrawer;
